import { create } from "zustand";
import type { PersistOptions } from "zustand/middleware";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { createActivitySlice } from "@/store/slices/activitySlice";
import { createBettingSlice } from "@/store/slices/bettingSlice";
import { createConfigSlice } from "@/store/slices/configSlice";
import { createMatchSlice } from "@/store/slices/matchSlice";
import { createPlayerSlice } from "@/store/slices/playerSlice";

import type { Slice } from "./createSlice";
import type { AppState, PersistedState } from "./types";
import { DEFAULT_MULTIPLIER_CONFIG, STORAGE_KEY } from "./types";

// ── Persist helpers ────────────────────────────────────────────

const isRecord = (val: unknown): val is Record<string, unknown> =>
  val !== null && typeof val === "object";

/** Parse raw persisted data into a valid `PersistedState`. */
export const parsePersistedState = (persisted: unknown): PersistedState => {
  const raw = isRecord(persisted) ? persisted : {};

  const config = isRecord(raw.multiplierConfig) ? raw.multiplierConfig : {};

  return {
    players: raw.players ?? {},
    activities: raw.activities ?? {},
    matches: raw.matches ?? {},
    results: raw.results ?? {},
    bets: raw.bets ?? {},
    lastStatusEvent: raw.lastStatusEvent ?? null,
    statusHistory: Array.isArray(raw.statusHistory) ? raw.statusHistory : [],
    multiplierConfig: { ...DEFAULT_MULTIPLIER_CONFIG, ...config },
  } satisfies Record<string, unknown> as PersistedState;
};

// ── Persist config ─────────────────────────────────────────────

const persistOptions: PersistOptions<AppState, PersistedState> = {
  name: STORAGE_KEY,

  partialize: (state): PersistedState => ({
    players: state.players,
    activities: state.activities,
    matches: state.matches,
    results: state.results,
    bets: state.bets,
    lastStatusEvent: state.lastStatusEvent,
    statusHistory: state.statusHistory,
    multiplierConfig: state.multiplierConfig,
  }),

  migrate: (persisted): PersistedState => parsePersistedState(persisted),

  version: 1,
};

// ── Compose slices ─────────────────────────────────────────────

const sliceCreator: Slice<AppState> = (set, get, store) => ({
  ...createPlayerSlice(set, get, store),
  ...createActivitySlice(set, get, store),
  ...createMatchSlice(set, get, store),
  ...createBettingSlice(set, get, store),
  ...createConfigSlice(set, get, store),

  /** Applies the given persisted state to the store. */
  applyNetworkState: (state) => {
    set((s) => {
      s.players = state.players;
      s.activities = state.activities;
      s.matches = state.matches;
      s.results = state.results;
      s.bets = state.bets;
      s.lastStatusEvent = state.lastStatusEvent;
      s.statusHistory = state.statusHistory ?? [];
      s.multiplierConfig = state.multiplierConfig;
    });
  },
});

// ── Store ──────────────────────────────────────────────────────

export const useStore = create<AppState>()(
  devtools(persist(immer(sliceCreator), persistOptions), { name: "openskill" }),
);

// ── Legacy compat: export/import helpers ───────────────────────

/**
 * Read-only snapshot for JSON export. This is the one legitimate use
 * of `getState()` — it only reads, never mutates.
 */
export const exportState = () => JSON.stringify(useStore.getState(), null, 2);
export const importStateFn = (json: string) => JSON.parse(json) as PersistedState;

// ── Re-exports ─────────────────────────────────────────────────
// Type re-exports for consumers that import from "../../state/state/store"
export type { AppState, PersistedState } from "./types";
export { DEFAULT_BANKROLL, DEFAULT_MULTIPLIER_CONFIG } from "./types";
